import clsx from "clsx";

export type Intent = 
  "primary" | "secondary" | "success" | "warning" | "danger" | "info" | "none" |
  "primary-inverse" | "secondary-inverse" | "success-inverse" | "warning-inverse" | "danger-inverse" | "info-inverse";

export function intentToClassName(intent?: Intent) {
  let className = "";
  switch (intent || "secondary") {
    case "primary": 
      className = clsx(
        "shadow-sm bg-blue-500",
        "text-white",
        "hover:bg-blue-600",
        "active:bg-blue-400",
        "focus:border-blue-400",
        "focus:border-offset-blue-100"
      );
      break;
    case "primary-inverse":
      className = clsx(
        "text-blue-500",
        "hover:bg-blue-50",
        "active:bg-blue-100",
        "focus:border-blue-400",
        "focus:border-offset-blue-100"
      );
      break;
    case "danger": 
      className = clsx(
        "shadow-sm",
        "bg-red-600",
        "text-white",
        "hover:bg-red-700",
        "active:bg-red-800",
        "focus:border-red-400",
        "focus:border-offset-red-100"
      );
      break;
    case "danger-inverse":
      className = clsx(
        "text-red-500",
        "hover:bg-red-50",
        "active:bg-red-100",
        "focus:border-red-400",
        "focus:border-offset-red-100"
      );
      break;
    case "warning": 
      className = clsx(
        "shadow-sm", 
        "bg-yellow-500",
        "text-black",
        "hover:bg-yellow-600",
        "active:bg-yellow-400",
        "focus:border-yellow-400",
        "focus:border-offset-yellow-100"
      );
      break;
    case "warning-inverse":
      className = clsx(
        "text-yellow-500",
        "hover:bg-yellow-50",
        "active:bg-yellow-100",
        "focus:border-yellow-400",
        "focus:border-offset-yellow-100"
      );
      break;
    case "success": 
      className = clsx(
        "shadow-sm bg-green-500",
        "text-white",
        "hover:bg-green-600",
        "active:bg-green-400",
        "focus:border-green-400",
        "focus:border-offset-green-100"
      );
      break;
    case "success-inverse":
      className = clsx(
        "text-green-500",
        "hover:bg-green-50",
        "active:bg-green-100",
        "focus:border-green-400",
        "focus:border-offset-green-100"
      );
      break;
    case "none": return "";
    case "secondary-inverse":
      className = clsx(
        "text-gray-500",
        "hover:bg-gray-50",
        "active:bg-gray-100",
        "focus:border-gray-400",
        "focus:border-offset-gray-100"
      );
      break;
    case "secondary":
    default:
      className = clsx(
        "shadow-sm bg-gray-100 dark:bg-black",
        "text-gray-700 dark:text-gray-50",
        "hover:bg-gray-200 dark:hover:bg-zinc-700",
        "active:bg-gray-300",
        "focus:border-gray-400",
        "focus:border-offset-gray-100"
      );
  }

  // common classes
  // No transition: the house motion rule is transform and opacity only, and a
  // Button animates neither — its hover/active feedback is a colour CHANGE,
  // which reads instantly. Every state above is kept; only the fade is gone.
  className = clsx(
    className,
    "active:shadow-none",
    "border border-transparent focus:outline-none focus:z-2"
  );

  return className;
}

/**
 * Returns solid background color class for an intent.
 * Use for non-interactive elements like dots, indicators, badges.
 * Unlike intentToClassName, this doesn't include hover/active/focus states.
 */
export function intentToSolidClassName(intent?: Intent): string {
  switch (intent) {
    case "primary":
    case "primary-inverse":
      return "bg-blue-500";
    case "danger":
    case "danger-inverse":
      return "bg-red-500";
    case "warning":
    case "warning-inverse":
      return "bg-yellow-500";
    case "success":
    case "success-inverse":
      return "bg-green-500";
    case "info":
    case "info-inverse":
      return "bg-blue-400";
    case "none":
      return "";
    case "secondary":
    case "secondary-inverse":
    default:
      return "bg-gray-500";
  }
}